import { Chip } from '@heroui/react';
import {
  type PatientMedicationItem,
  usePatientMedicationListQuery,
} from '@/api/patient-medication';
import { medicationMealRelationMap } from '@/lib/health-plan-enums';

// 患者在小程序自行添加的用药 (手填或从 HIS 处方加入), 后台只读展示: 医生能看见患者在吃什么, 不能改、不能停
// 方案工作台的用药模块和患者详情的方案卡片共用
export function PatientSelfMedicationList({
  accountId,
  compact = false,
}: {
  accountId: string;
  // 患者详情卡片里用紧凑文本行, 方案工作台用卡片
  compact?: boolean;
}) {
  const query = usePatientMedicationListQuery(accountId);
  const items = (query.data?.items ?? []).filter(
    (item) => item.addedBy === 'patient' && !item.archivedAt,
  );

  if (query.isLoading) {
    return (
      <div className="text-muted text-[13px]">正在读取患者自行添加的用药</div>
    );
  }
  if (query.isError) {
    return (
      <div className="text-warning text-[13px]">患者自行添加的用药读取失败</div>
    );
  }
  if (items.length === 0) {
    return null;
  }

  if (compact) {
    return (
      <div className="space-y-1">
        {items.map((item) => (
          <div key={item.id} className="flex flex-wrap items-center gap-2">
            <Chip size="sm" variant="soft" color="accent">
              患者自行添加
            </Chip>
            {item.origin === 'his' && (
              <Chip size="sm" variant="soft">
                HIS 处方
              </Chip>
            )}
            <span className="text-foreground text-[13px] leading-5">
              {describeSelfMedication(item)}
            </span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="mt-3 rounded-lg border border-accent/30 bg-accent-soft/20 p-3">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <div className="text-foreground text-[13px] font-extrabold">
          患者自行添加的用药
        </div>
        <Chip size="sm" variant="soft">
          {items.length}
        </Chip>
        <span className="text-muted text-[12px]">
          来自小程序, 患者自己维护; 后台仅可查看
        </span>
      </div>
      <div className="flex flex-col gap-2">
        {items.map((item) => (
          <div
            key={item.id}
            className="rounded-lg border border-border bg-background px-3 py-2"
          >
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-foreground text-[14px] font-bold">
                {item.drugName}
              </span>
              {item.specification && (
                <span className="text-muted text-[12px]">
                  {item.specification}
                </span>
              )}
              {item.origin === 'his' && (
                <Chip size="sm" variant="soft">
                  HIS 处方
                </Chip>
              )}
            </div>
            <div className="text-muted mt-1 text-[12px] leading-5">
              {describeSelfMedicationDetail(item)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// describeSelfMedication 一行文本: 药名 + 明细, 供患者详情的紧凑列表
function describeSelfMedication(item: PatientMedicationItem) {
  const detail = describeSelfMedicationDetail(item);
  return detail ? `${item.drugName}，${detail}` : item.drugName;
}

// describeSelfMedicationDetail 用量 / 服药安排 / 与餐关系 / 疗程 / 提醒状态
//
// 每次用量不一样时 (早 1 片晚 2 片) 逐个剂次列出来, 一样就只写一次
function describeSelfMedicationDetail(item: PatientMedicationItem) {
  const parts: string[] = [];
  const defaultDosage = `${item.dosage}${item.dosageUnit}`.trim();
  const scheduled = item.doses.filter((dose) => dose.time);
  const perDoseDosage = scheduled.some((dose) => dose.dosage);
  if (defaultDosage && !perDoseDosage) parts.push(`每次 ${defaultDosage}`);
  parts.push(
    scheduled.length > 0
      ? `每日 ${scheduled.length} 次（${scheduled
          .map((dose) => {
            // 数量和单位各自继承: 只填了数量时单位沿用该药的默认单位
            const dosage = dose.dosage
              ? `${dose.dosage}${dose.dosageUnit || item.dosageUnit}`.trim()
              : defaultDosage;
            return perDoseDosage && dosage
              ? `${dose.time} ${dosage}`
              : dose.time;
          })
          .join('、')}）`
      : '必要时服用',
  );
  const meal = medicationMealRelationMap[item.mealRelation];
  if (meal) parts.push(meal);
  parts.push(
    item.endDate
      ? `${item.startDate} 至 ${item.endDate}`
      : `${item.startDate} 起长期`,
  );
  if (scheduled.length > 0 && !item.reminderEnabled) {
    parts.push('患者已关闭提醒');
  }
  return parts.join('，');
}
